import { Node, mergeAttributes } from "@tiptap/core";
import { NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer, ReactNodeViewProps } from "@tiptap/react";
import { DiffButtons } from "../ui/diffButtons";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    diffBlock: {
      unwrapDiffBlock: (options: { pos: number; nodeSize: number }) => ReturnType;
      removeDiffBlock: (options: { pos: number; nodeSize: number }) => ReturnType;
      acceptDiffBlock: (options: { pos: number; nodeSize: number; diffType: string }) => ReturnType;
      rejectDiffBlock: (options: { pos: number; nodeSize: number; diffType: string }) => ReturnType;
    };
  }
}

function DiffBlockComponent({ node, getPos, editor }: ReactNodeViewProps) {
  const diffType = node.attrs.diffType as "add" | "del" | "equal";

  const accept = () => {
    const pos = getPos();
    if (pos === undefined) return;
    editor.commands.acceptDiffBlock({ pos, nodeSize: node.nodeSize, diffType });
  };

  const discard = () => {
    const pos = getPos();
    if (pos === undefined) return;
    editor.commands.rejectDiffBlock({ pos, nodeSize: node.nodeSize, diffType });
  };

  if (diffType === "equal") {
    return (
      <NodeViewWrapper as="div" className="diff-block">
        <NodeViewContent className="flex-1 prose" contentEditable={true} />
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      as="div"
      data-diff-type={diffType}
      className={`
        diff-block relative group rounded
        ${diffType === "del" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}
      `}
    >
      <NodeViewContent className="flex-1 prose" contentEditable={diffType !== "del"} />
      <DiffButtons accept={accept} discard={discard} type={diffType} />
    </NodeViewWrapper>
  );
}

export const DiffBlock = Node.create({
  name: "diffBlock",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      diffType: {
        default: "add",
        parseHTML: (el) => el.getAttribute("data-diff-type") ?? "add",
        renderHTML: (attrs) => ({ "data-diff-type": attrs.diffType })
      }
    };
  },

  parseHTML() {
    return [{ tag: "div[data-diff-type]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { class: "diff-block" }), 0];
  },

  addCommands() {
    return {
      unwrapDiffBlock:
        (options) =>
        ({ tr, state, dispatch }) => {
          if (dispatch) {
            const { pos, nodeSize } = options;
            const node = state.doc.nodeAt(pos);
            if (node && pos + nodeSize <= state.doc.content.size) {
              tr.replaceWith(pos, pos + nodeSize, node.content);
            }
          }
          return true;
        },

      removeDiffBlock:
        (options) =>
        ({ tr, state, dispatch }) => {
          if (dispatch) {
            const { pos, nodeSize } = options;
            if (pos + nodeSize <= state.doc.content.size) {
              tr.delete(pos, pos + nodeSize);
            }
          }
          return true;
        },

      acceptDiffBlock:
        (options) =>
        ({ commands }) => {
          const { pos, nodeSize, diffType } = options;
          if (diffType === "del") {
            return commands.removeDiffBlock({ pos, nodeSize });
          } else {
            return commands.unwrapDiffBlock({ pos, nodeSize });
          }
        },

      rejectDiffBlock:
        (options) =>
        ({ commands }) => {
          const { pos, nodeSize, diffType } = options;
          if (diffType === "add") {
            return commands.removeDiffBlock({ pos, nodeSize });
          } else {
            return commands.unwrapDiffBlock({ pos, nodeSize });
          }
        }
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(DiffBlockComponent);
  }
});
